import { createAssistantStream } from 'assistant-stream';

import type { RemoteThreadListAdapter } from '@assistant-ui/react';
import type { AppRouter } from '@cadstrom/backend/trpc';
import type { AgentChat } from '@cadstrom/cadstrom-shared';
import type { TRPCClient } from '@trpc/client';

// `@assistant-ui/core` is a transitive dep — derive the related types off the
// adapter shape so we don't have to require a direct dependency.
type RemoteThreadListResponse = Awaited<ReturnType<RemoteThreadListAdapter['list']>>;
type RemoteThreadInitializeResponse = Awaited<ReturnType<RemoteThreadListAdapter['initialize']>>;
type RemoteThreadMetadata = RemoteThreadListResponse['threads'][number];

interface BuildAdapterArgs {
	/**
	 * The agent new threads from this surface run. Take it from
	 * {@link AgentChat.CHAT_SURFACE_AGENT_IDS} rather than writing an id inline.
	 */
	agentId: AgentChat.ChatSurfaceAgentId;
	ownerId?: string;
	projectId?: string;
	revisionId?: string;
	packageId?: string;
	trpcClient: TRPCClient<AppRouter>;
}

export interface AgentWorkbenchThreadListAdapter {
	adapter: RemoteThreadListAdapter;
	/** Resolves once the initial `list()` call has settled, preventing a race where a user clicks a thread before the list has hydrated. */
	listReady: Promise<void>;
}

/**
 * Per-thread metadata the adapter stashes on `RemoteThreadMetadata.custom` so the
 * thread-list UI can render timestamps without a second round-trip. Dates arrive
 * from tRPC as `Date` instances via superjson.
 */
export interface AgentWorkbenchThreadCustom {
	lastActivityAt: Date;
	createdAt: Date;
}

/**
 * Builds a {@link RemoteThreadListAdapter} backed by the `agentChatConversation` tRPC router.
 *
 * `list()` is intentionally unscoped by project/revision. By default it returns
 * the actor's conversations; when `ownerId` is supplied it stays fixed to that
 * accessible owner's history. Project/revision only tag new threads.
 *
 * `agentId` applies only to threads this adapter creates; an existing thread
 * keeps the agent it was created with.
 *
 * Unsupported methods are stubbed as no-ops.
 */
export function buildAgentWorkbenchThreadListAdapter({
	agentId,
	ownerId,
	projectId,
	revisionId,
	packageId,
	trpcClient,
}: BuildAdapterArgs): AgentWorkbenchThreadListAdapter {
	let resolveListReady: () => void;
	const listReady = new Promise<void>((resolve) => {
		resolveListReady = resolve;
	});
	let firstListSettled = false;
	const settleListReady = (): void => {
		if (firstListSettled) {
			return;
		}
		firstListSettled = true;
		resolveListReady();
	};

	// Pre-generated sessionId for the next initialize() call. Rotated after each
	// successful creation so every new thread gets a unique (userId, sessionId) key.
	let pendingSessionId = crypto.randomUUID();
	let cachedInit: Promise<RemoteThreadInitializeResponse> | undefined;

	type ListResult = Awaited<ReturnType<typeof trpcClient.agentChatConversation.list.query>>;
	type ConversationSummary = ListResult['items'][number];

	const summaryToMetadata = (c: ConversationSummary): RemoteThreadMetadata => ({
		status: 'regular',
		remoteId: c.id,
		externalId: c.id,
		title: c.title ?? undefined,
		custom: { lastActivityAt: c.lastActivityAt, createdAt: c.createdAt } satisfies AgentWorkbenchThreadCustom,
	});

	const adapter: RemoteThreadListAdapter = {
		list: async (): Promise<RemoteThreadListResponse> => {
			try {
				// Unscoped on purpose — see the function docstring. Capped at 100;
				// the adapter contract doesn't surface pagination.
				const result = await trpcClient.agentChatConversation.list.query({
					limit: 100,
					...(ownerId ? { ownerId } : {}),
				});
				return { threads: result.items.map(summaryToMetadata) };
			} finally {
				settleListReady();
			}
		},

		initialize: (): Promise<RemoteThreadInitializeResponse> => {
			cachedInit ??= trpcClient.agentChatConversation.create
				.mutate({ projectId, revisionId, packageId, sessionId: pendingSessionId, agentId })
				.then((summary) => {
					cachedInit = undefined;
					pendingSessionId = crypto.randomUUID();
					return { remoteId: summary.id, externalId: summary.id };
				})
				.catch((err: unknown) => {
					cachedInit = undefined;
					pendingSessionId = crypto.randomUUID();
					throw err;
				});
			return cachedInit;
		},

		delete: async (conversationId: string): Promise<void> => {
			await trpcClient.agentChatConversation.delete.mutate({ conversationId });
		},

		// Required by the interface but not yet implemented.
		archive: async () => {},
		unarchive: async () => {},
		rename: async () => {},

		// assistant-ui auto-invokes this once the first turn of a new conversation
		// ends. The backend derives a concise title from the opening exchange,
		// persists it, and returns it; we stream it back so the active thread's
		// title updates in place with no reload.
		generateTitle: async (conversationId: string) => {
			const { title } = await trpcClient.agentChatConversation.generateTitle.mutate({ conversationId });
			return createAssistantStream((controller) => {
				if (title) {
					controller.appendText(title);
				}
			});
		},
		fetch: async (conversationId: string): Promise<RemoteThreadMetadata> => {
			const conversation = await trpcClient.agentChatConversation.get.query({
				conversationId,
				...(ownerId ? { ownerId } : {}),
			});
			if (!conversation) {
				throw new Error('Conversation not found');
			}
			return summaryToMetadata(conversation);
		},
	};

	return {
		adapter,
		listReady,
	};
}
